import React, { useState, useRef, useEffect } from 'react';
import {
  Send, Paperclip, Smile, Loader2, Mic,
  Image as ImageIcon, Music, FileText, Film,
  X
} from 'lucide-react';

interface MessageInputFieldProps {
  value: string;
  onChange: (value: string) => void;
  onSendMessage: () => void;
  onSendMedia: (file: File) => void;
  onStartTyping?: () => void;
  onStopTyping?: () => void;
  isLoading?: boolean;
  isUploading?: boolean;
  disabled?: boolean;
  placeholder?: string;
  maxLength?: number;
  allowMediaTypes?: ('image' | 'video' | 'audio' | 'document')[];
  selectedMediaPreview?: { file: File; type: string } | null;
  onRemoveMedia?: () => void;
}

const MessageInputField: React.FC<MessageInputFieldProps> = ({
  value,
  onChange,
  onSendMessage,
  onSendMedia,
  onStartTyping,
  onStopTyping,
  isLoading = false,
  isUploading = false,
  disabled = false,
  placeholder = 'Type a message...',
  maxLength = 5000,
  allowMediaTypes = ['image', 'video', 'audio', 'document'],
  selectedMediaPreview,
  onRemoveMedia
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(
        textareaRef.current.scrollHeight,
        120
      ) + 'px';
    }
  }, [value]);

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value.slice(0, maxLength);
    onChange(newValue);

    // Handle typing indicators
    if (onStartTyping && newValue.length > 0) {
      onStartTyping();
    }

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    if (onStopTyping && newValue.length === 0) {
      onStopTyping();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !isLoading && !isUploading) {
      e.preventDefault();
      if (value.trim()) {
        onSendMessage();
      }
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      onSendMedia(file);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const getAcceptAttribute = () => {
    const typeMap: Record<string, string> = {
      image: 'image/*',
      video: 'video/*',
      audio: 'audio/*',
      document: '.pdf,.doc,.docx,.xls,.xlsx,.txt,.ppt,.pptx'
    };

    return allowMediaTypes.map(t => typeMap[t]).join(',');
  };

  const canSendMessage = value.trim().length > 0 && !isLoading && !isUploading;

  return (
    <div className="bg-light-card dark:bg-dark-card border-t border-light-border dark:border-dark-border p-4 shrink-0 space-y-2">
      {/* Media Preview */}
      {selectedMediaPreview && (
        <div className="flex items-center gap-2 p-3 bg-light-bg dark:bg-dark-bg rounded-lg border border-light-border dark:border-dark-border">
          <div className="flex-1 flex items-center gap-2 text-sm">
            {selectedMediaPreview.type.startsWith('image') && (
              <ImageIcon className="h-4 w-4 text-primary dark:text-golden" />
            )}
            {selectedMediaPreview.type.startsWith('video') && (
              <Film className="h-4 w-4 text-primary dark:text-golden" />
            )}
            {selectedMediaPreview.type.startsWith('audio') && (
              <Music className="h-4 w-4 text-primary dark:text-golden" />
            )}
            {selectedMediaPreview.type !== 'image' &&
              selectedMediaPreview.type !== 'video' &&
              selectedMediaPreview.type !== 'audio' && (
                <FileText className="h-4 w-4 text-primary dark:text-golden" />
              )}
            <span className="text-light-text dark:text-dark-text truncate">
              {selectedMediaPreview.file.name}
            </span>
          </div>
          <button
            onClick={onRemoveMedia}
            className="p-1 rounded hover:bg-light-border dark:hover:bg-dark-border transition text-light-subtle dark:text-dark-subtle"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Input Container */}
      <div className="flex items-end gap-2">
        {/* Attach Button */}
        <div className="relative">
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleFileSelect}
            accept={getAcceptAttribute()}
            className="hidden"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            className="p-2.5 rounded-lg hover:bg-light-border dark:hover:bg-dark-border transition text-light-subtle dark:text-dark-subtle hover:text-light-text dark:hover:text-dark-text"
            disabled={isUploading}
            title="Attach file"
          >
            {isUploading ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <Paperclip className="h-5 w-5" />
            )}
          </button>
        </div>

        {/* Message Input */}
        <div className="flex-1 bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border rounded-xl flex items-end px-4 py-2.5 focus-within:ring-2 focus-within:ring-primary/50 dark:focus-within:ring-golden/50 focus-within:border-transparent transition">
          <textarea
            ref={textareaRef}
            value={value}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className="w-full bg-transparent border-none outline-none resize-none text-light-text dark:text-dark-text placeholder-light-subtle dark:placeholder-dark-subtle max-h-32 py-1"
            rows={1}
            disabled={disabled}
            maxLength={maxLength}
          />
          <button
            className="ml-2 text-light-subtle dark:text-dark-subtle hover:text-light-text dark:hover:text-dark-text transition"
            title="Emoji"
          >
            <Smile className="h-5 w-5" />
          </button>
        </div>

        {/* Send Button */}
        {canSendMessage ? (
          <button
            onClick={onSendMessage}
            className="p-2.5 rounded-lg bg-primary dark:bg-golden text-white hover:opacity-90 transition-all disabled:opacity-50"
            disabled={isLoading}
            title="Send message"
          >
            {isLoading ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <Send className="h-5 w-5" />
            )}
          </button>
        ) : (
          <button
            className="p-2.5 rounded-lg bg-light-bg dark:bg-dark-bg text-light-subtle dark:text-dark-subtle hover:bg-light-border dark:hover:bg-dark-border transition-all"
            title="Record voice message"
          >
            <Mic className="h-5 w-5" />
          </button>
        )}
      </div>

      {/* Character Count */}
      {value.length > maxLength * 0.9 && (
        <div className="text-right text-xs text-light-subtle dark:text-dark-subtle">
          {value.length} / {maxLength}
        </div>
      )}
    </div>
  );
};

export default MessageInputField;
